import type { EmployeeSummary, EmployeeRole } from "@/hooks/useMakersBoardData";
import { useRoles } from "@/hooks/useRoles";
import { RoleBadges } from "@/components/RoleBadges";
import { useRecentlyChanged } from "@/contexts/RecentlyChangedRowsContext";
import { useOoo } from "@/contexts/OooContext";
import { buildEmployeeRowModel, type AvailabilityMode } from "@/utils/makersUtils";
import { formatProjectDisplayName } from "@/utils/projectUtils";
import { getEmployeeDisplayName } from "@/utils/nameUtils";
import { calculateFloatingDropdownPosition } from "@/utils/floatingDropdown";
import type { WeekColumn } from "@/utils/weekUtils";
import { Link } from "@tanstack/react-router";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { TimelineRow } from "./TimelineRow";
import { oooForTimelineRow } from "@/utils/oooDisplay";

interface EmployeeRowProps {
  employee: EmployeeSummary;
  weeks: WeekColumn[];
  roleOptions: string[];
  updateCapacityOptimistically: (
    employeeId: string,
    newCapacity: number
  ) => void;
  updateEmployeeCapacity: (
    employeeId: string,
    newCapacity: number
  ) => Promise<void>;
  updateEmployeeRoles: (
    employeeId: string,
    roles: EmployeeRole[]
  ) => Promise<void>;
  updateRolesOptimistically: (
    employeeId: string,
    roles: EmployeeRole[]
  ) => void;
  onClick?: (employeeId: string) => void;
  isSelected?: boolean;
  /** When true, role is shown as read-only text (no dropdown). Used e.g. in Find People modal. */
  roleEditingDisabled?: boolean;
  availabilityMode?: AvailabilityMode;
  /** When provided, a delete button is shown. Used for manually-created employees (employee_number IS NULL). */
  onDelete?: (employeeId: string) => Promise<void>;
  /** When provided, the employee name is editable inline. Used for manually-created employees (employee_number IS NULL). */
  onRename?: (employeeId: string, newName: string) => Promise<void>;
  updateNameOptimistically?: (employeeId: string, newName: string) => void;
  /** When provided, Bamboo-synced employees can set or clear an app-managed nickname. */
  onNicknameChange?: (employeeId: string, nickname: string | null) => Promise<void>;
  updateNicknameOptimistically?: (employeeId: string, nickname: string | null) => void;
}

/**
 * Row component displaying a single employee with expandable project breakdown
 */
export function EmployeeRow({
  employee,
  weeks,
  roleOptions,
  updateCapacityOptimistically,
  updateEmployeeCapacity,
  updateEmployeeRoles,
  updateRolesOptimistically,
  onClick,
  isSelected = false,
  roleEditingDisabled = false,
  availabilityMode = "all",
  onDelete,
  onRename,
  updateNameOptimistically,
  onNicknameChange,
  updateNicknameOptimistically,
}: EmployeeRowProps) {
  const { formatRole, roleSortOrder } = useRoles();
  const { oooByEmployee, setOoo, enabled: oooEnabled, editMode: oooEditMode } = useOoo();
  // A row in "Add OOO" mode shows every stored value so it can be edited;
  // otherwise only weeks that reach the display threshold get an overlay. Rows
  // that can't edit (e.g. Find People, where role editing is disabled) stay
  // filtered even while the shared edit mode is on.
  const oooEditable = oooEnabled && oooEditMode && !roleEditingDisabled;
  const storedOoo = oooByEmployee.get(employee.id);
  const oooByWeekKey = useMemo(
    () => oooForTimelineRow(storedOoo, employee.weekly_capacity_hours, oooEditable),
    [oooEditable, storedOoo, employee.weekly_capacity_hours],
  );
  const [expanded, setExpanded] = useState(false);
  const [editingCapacity, setEditingCapacity] = useState(false);
  const [editingRole, setEditingRole] = useState(false);
  const [editingRoleSelection, setEditingRoleSelection] = useState<EmployeeRole[]>([]);
  const [capacityValue, setCapacityValue] = useState(
    String(employee.weekly_capacity_hours)
  );
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(employee.name);
  const [savingName, setSavingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const roleDropdownAnchorRef = useRef<HTMLDivElement>(null);
  const roleDropdownContainerRef = useRef<HTMLDivElement>(null);
  const roleDropdownNaturalSizeRef = useRef<{
    width: number;
    height: number;
  } | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number;
    left: number;
    maxHeight: number;
    maxWidth: number;
    ready: boolean;
  } | null>(null);

  const currentRoles = useMemo(
    () => (employee.role ?? []).filter(Boolean) as EmployeeRole[],
    [employee.role]
  );

  const model = useMemo(
    () => buildEmployeeRowModel(employee, weeks, availabilityMode),
    [employee, weeks, availabilityMode]
  );
  const editsNickname = Boolean(employee.employee_number && onNicknameChange);
  const displayName = getEmployeeDisplayName(employee);
  const originalName = employee.nickname?.trim() ? employee.name.trim() : "";
  const editableDisplayName = Boolean(onRename || editsNickname);

  const displayNameContent = (
    <>
      {displayName}
      {originalName ? (
        <span className="employee-row__original-name" data-testid="employee-original-name">
          {` (${originalName})`}
        </span>
      ) : null}
    </>
  );

  const resetNameValue = () => {
    setNameValue(editsNickname ? (employee.nickname ?? "") : employee.name);
  };

  const handleSaveCapacity = async () => {
    const newCapacity = Number(capacityValue);
    if (newCapacity === employee.weekly_capacity_hours) {
      setEditingCapacity(false);
      return;
    }
    if (
      capacityValue === "" ||
      isNaN(newCapacity) ||
      newCapacity < 0 ||
      newCapacity > 80
    ) {
      setCapacityValue(String(employee.weekly_capacity_hours));
      return;
    }
    if (newCapacity === employee.weekly_capacity_hours) {
      setEditingCapacity(false);
      return;
    }
    updateCapacityOptimistically(employee.id, newCapacity);
    setEditingCapacity(false);

    await updateEmployeeCapacity(employee.id, newCapacity);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSaveCapacity();
    } else if (e.key === "Escape") {
      setCapacityValue(String(employee.weekly_capacity_hours));
      setEditingCapacity(false);
    }
  };

  const persistNameValue = async (rawValue: string) => {
    if (!onRename && !onNicknameChange) return;
    const trimmed = rawValue.trim();
    if (!editsNickname && !trimmed) {
      resetNameValue();
      setEditingName(false);
      return;
    }
    const nextValue = editsNickname ? (trimmed || null) : trimmed;
    const previousValue = editsNickname ? employee.nickname : employee.name;
    if (nextValue === (previousValue ?? null)) {
      setEditingName(false);
      return;
    }
    setSavingName(true);
    try {
      if (editsNickname) {
        updateNicknameOptimistically?.(employee.id, nextValue);
        await onNicknameChange?.(employee.id, nextValue);
      } else {
        updateNameOptimistically?.(employee.id, trimmed);
        await onRename?.(employee.id, trimmed);
      }
    } catch (error) {
      console.error("Failed to update employee display name", error);
      if (editsNickname) {
        updateNicknameOptimistically?.(employee.id, previousValue);
      } else if (previousValue) {
        updateNameOptimistically?.(employee.id, previousValue);
      }
      setNameValue(previousValue ?? "");
    } finally {
      setSavingName(false);
      setEditingName(false);
    }
  };

  const handleSaveName = () => {
    void persistNameValue(nameValue);
  };

  const handleResetNickname = () => {
    if (!editsNickname || savingName) return;
    setNameValue("");
    void persistNameValue("");
  };

  const handleNameKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSaveName();
    } else if (e.key === "Escape") {
      e.preventDefault();
      resetNameValue();
      setEditingName(false);
    }
  };

  const updateDropdownPosition = useCallback(() => {
    const anchor = roleDropdownAnchorRef.current;
    if (!anchor) return;

    const anchorRect = anchor.getBoundingClientRect();
    const dropdown = roleDropdownContainerRef.current;
    let naturalSize = roleDropdownNaturalSizeRef.current;

    if (dropdown && !naturalSize) {
      const dropdownRect = dropdown.getBoundingClientRect();
      naturalSize = {
        width: dropdownRect.width,
        height: dropdownRect.height,
      };
      roleDropdownNaturalSizeRef.current = naturalSize;
    }

    const position = calculateFloatingDropdownPosition({
      anchorRect,
      dropdownWidth: naturalSize?.width ?? 160,
      dropdownHeight: naturalSize?.height ?? 0,
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
    });
    const next = {
      top: position.top,
      left: position.left,
      maxHeight: position.maxHeight,
      maxWidth: position.maxWidth,
      ready: Boolean(naturalSize),
    };

    setDropdownPosition((current) =>
      current &&
      current.top === next.top &&
      current.left === next.left &&
      current.maxHeight === next.maxHeight &&
      current.maxWidth === next.maxWidth &&
      current.ready === next.ready
        ? current
        : next
    );
  }, []);

  useLayoutEffect(() => {
    if (!editingRole || !dropdownPosition || dropdownPosition.ready) return;
    updateDropdownPosition();
  }, [dropdownPosition, editingRole, updateDropdownPosition]);

  useEffect(() => {
    if (!editingRole) {
      setDropdownPosition(null);
      return;
    }
    updateDropdownPosition();
    const scrollEl = roleDropdownAnchorRef.current?.closest(
      ".makers-table__body-scroll"
    );
    // Anchors role dropdown to the employee row
    const raf = { id: 0 };
    const scheduleUpdate = () => {
      if (raf.id) cancelAnimationFrame(raf.id);
      raf.id = requestAnimationFrame(() => {
        raf.id = 0;
        updateDropdownPosition();
      });
    };
    scrollEl?.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("scroll", scheduleUpdate, true);
    window.addEventListener("resize", scheduleUpdate);
    return () => {
      scrollEl?.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("scroll", scheduleUpdate, true);
      window.removeEventListener("resize", scheduleUpdate);
      if (raf.id) cancelAnimationFrame(raf.id);
    };
  }, [editingRole, updateDropdownPosition]);

  useEffect(() => {
    if (!editingRole) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        roleDropdownAnchorRef.current?.contains(target) ||
        roleDropdownContainerRef.current?.contains(target)
      ) {
        return;
      }
      setEditingRole(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [editingRole]);

  const handleOpenRoleEditor = () => {
    roleDropdownNaturalSizeRef.current = null;
    setEditingRoleSelection([...currentRoles]);
    updateDropdownPosition();
    setEditingRole(true);
  };

  const handleSaveRoles = async (selected: EmployeeRole[]) => {
    if (selected.length === 0) return; // Validate: at least one role
    const sorted = [...selected].sort((a, b) => roleSortOrder(a) - roleSortOrder(b));
    updateRolesOptimistically(employee.id, sorted);
    setEditingRole(false);
    setSaving(true);
    try {
      await updateEmployeeRoles(employee.id, sorted);
    } catch {
      updateRolesOptimistically(employee.id, currentRoles);
    } finally {
      setSaving(false);
    }
  };

  const isFlashing = useRecentlyChanged("employees", employee.id);

  const containerClassName = [
    "employee-row-container",
    expanded ? "employee-row-container--expanded" : "",
    onClick ? "employee-row-container--clickable" : "",
    model.isOverAllocated ? "employee-row-container--over-allocated" : "",
    isSelected ? "employee-row-container--selected" : "",
    isFlashing ? "row--flashing" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={containerClassName} data-testid="employee-row" data-employee-id={employee.id} data-employee-name={employee.name} data-employee-display-name={displayName} onClick={() => onClick?.(employee.id)}>
      <div className="employee-row-container__fixed">
        <div className="employee-row__fixed employee-row--main">
          {onClick && (
            <input
              type="checkbox"
              className="employee-row__select-checkbox"
              checked={isSelected}
              onChange={() => onClick(employee.id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${displayName}`}
            />
          )}
          <div className="employee-row__info">
            <div className="employee-row__name">
              {editableDisplayName ? (
                editingName ? (
                  <span className="employee-row__name-editor">
                    <input
                      type="text"
                      className="employee-row__name-input"
                      value={nameValue}
                      onChange={(e) => setNameValue(e.target.value)}
                      onBlur={handleSaveName}
                      onKeyDown={handleNameKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      disabled={savingName}
                      aria-label={editsNickname ? "Employee nickname" : "Employee name"}
                      placeholder={editsNickname ? "nickname: e.g Viv" : "Employee name"}
                    />
                    {editsNickname && nameValue.trim() && (
                      <button
                        type="button"
                        className="employee-row__nickname-reset"
                        data-testid="employee-nickname-reset"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleResetNickname();
                        }}
                        disabled={savingName}
                        aria-label="Reset nickname to original name"
                        title={`Use original name: ${employee.name}`}
                      >
                        <span aria-hidden>↺</span>
                      </button>
                    )}
                  </span>
                ) : (
                  <span
                    className="employee-row__name-editable"
                    data-testid="employee-display-name"
                    onClick={(e) => {
                      e.stopPropagation();
                      resetNameValue();
                      setEditingName(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        resetNameValue();
                        setEditingName(true);
                      }
                    }}
                  >
                    {displayNameContent}
                  </span>
                )
              ) : (
                <span data-testid="employee-display-name">{displayNameContent}</span>
              )}
              {onDelete && (
                <button
                  type="button"
                  className="employee-row__delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(employee.id);
                  }}
                  aria-label="Delete employee"
                  title="Delete employee"
                >
                  &#128465;
                </button>
              )}
            </div>
            <div className="employee-row__details">
              {model.hasMultipleProjects && (
                <button
                  className={`employee-row__toggle ${expanded ? "employee-row__toggle--open" : ""
                    }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((prev) => !prev);
                  }}
                  type="button"
                  aria-label={expanded ? "Collapse projects" : "Expand projects"}
                />
              )}
              <div className="employee-row__detail-rows">
                <div className="employee-row__meta">
                  <div ref={roleDropdownAnchorRef}>
                    {roleEditingDisabled ? (
                      <span className="employee-row__role-readonly">
                        <RoleBadges roles={(model.employee.role ?? []) as string[]} />
                      </span>
                    ) : editingRole ? (
                      <span
                        className="employee-row__role-placeholder"
                        aria-hidden
                      >
                        <RoleBadges roles={(model.employee.role ?? []) as string[]} />
                      </span>
                    ) : (
                      <span
                        className="employee-row__role-editable"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenRoleEditor();
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOpenRoleEditor();
                          }
                        }}
                      >
                        <RoleBadges roles={(model.employee.role ?? []) as string[]} />
                      </span>
                    )}
                  </div>
                  {!roleEditingDisabled && editingRole &&
                    dropdownPosition &&
                    createPortal(
                      <div
                        ref={roleDropdownContainerRef}
                        className="employee-row__role-dropdown employee-row__role-dropdown--portal"
                        style={{
                          position: "fixed",
                          top: dropdownPosition.top,
                          left: dropdownPosition.left,
                          maxHeight: dropdownPosition.ready
                            ? dropdownPosition.maxHeight
                            : undefined,
                          maxWidth: dropdownPosition.ready
                            ? dropdownPosition.maxWidth
                            : undefined,
                          visibility: dropdownPosition.ready
                            ? "visible"
                            : "hidden",
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="employee-row__role-dropdown-list">
                          {roleOptions.map((role) => {
                            const isChecked = editingRoleSelection.includes(
                              role as EmployeeRole
                            );
                            return (
                              <label
                                key={role}
                                className="employee-row__role-option"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <input
                                  type="checkbox"
                                  checked={isChecked}
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    const next = e.target.checked
                                      ? [
                                          ...editingRoleSelection,
                                          role as EmployeeRole,
                                        ]
                                      : editingRoleSelection.filter(
                                          (r) => r !== role
                                        );
                                    setEditingRoleSelection(next);
                                  }}
                                />
                                {formatRole(role)}
                              </label>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="employee-row__role-done"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveRoles(editingRoleSelection);
                          }}
                          disabled={
                            saving || editingRoleSelection.length === 0
                          }
                        >
                          Done
                        </button>
                      </div>,
                      document.body
                    )}
                  {model.employee.office?.name ? (
                    <>
                      <span className="divider">•</span>
                      {model.employee.office.name}
                    </>
                  ) : null}
                  <span className="divider">•</span>
                  {editingCapacity ? (
                    <input
                      type="number"
                      className="employee-row__capacity-input"
                      value={capacityValue}
                      onChange={(e) => setCapacityValue(e.target.value)}
                      onBlur={handleSaveCapacity}
                      onKeyDown={handleKeyDown}
                      onClick={(e) => e.stopPropagation()}
                      min={0}
                      max={80}
                      step={0.5}
                      autoFocus
                      disabled={saving}
                    />
                  ) : (
                    <span
                      className="employee-row__capacity-editable"
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingCapacity(true);
                      }}
                    >
                      {model.employee.weeklyCapacityHours}h/wk
                    </span>
                  )}
                </div>
                {!expanded && (
                  <div className="employee-row__meta employee-row__projects-list">
                    {model.projects.map((projectAllocation, index) => {
                      const displayName = formatProjectDisplayName(
                        projectAllocation.project?.name,
                        projectAllocation.project?.client,
                      );
                      return (
                        <span key={projectAllocation.projectId ?? `no-project-${index}`}>
                          {index > 0 && ", "}
                          {projectAllocation.projectId ? (
                            <Link
                              to="/"
                              search={{ project: projectAllocation.projectId }}
                              onClick={(e) => e.stopPropagation()}
                              title={displayName}
                            >
                              {displayName}
                            </Link>
                          ) : (
                            <span title={displayName}>{displayName}</span>
                          )}
                        </span>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {expanded && model.hasAssignments && (
          <>
            {model.projects.map((projectAllocation) => {
              const displayName = formatProjectDisplayName(
                projectAllocation.project?.name,
                projectAllocation.project?.client,
              );
              return (
                <div
                  key={projectAllocation.projectId ?? "no-project"}
                  className="employee-row__fixed employee-row--sub"
                >
                  <span className="employee-row__indent">└</span>
                  {projectAllocation.projectId ? (
                    <Link
                      to="/"
                      search={{ project: projectAllocation.projectId }}
                      onClick={(e) => e.stopPropagation()}
                      className="employee-row__project-row"
                      title={displayName}
                    >
                      {displayName}
                    </Link>
                  ) : (
                    <span
                      className="employee-row__project-row"
                      title={displayName}
                    >
                      {displayName}
                    </span>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className="employee-row-container__timeline">
        <TimelineRow
          weeks={weeks}
          className="employee-row--main"
          totalWeeklyHours={model.employee.weeklyCapacityHours}
          projectAllocations={model.projects}
          hoursLabel="Total"
          oooByWeekKey={oooByWeekKey}
          oooEditable={oooEditable}
          onOooChange={(weekKeys, hours) =>
            setOoo(employee.id, weekKeys, hours, {
              employeeName: employee.name,
              officeId: employee.office_id,
            })
          }
        />

        {expanded && model.hasAssignments && (
          <>
            {model.projects.map((projectAllocation) => (
              <TimelineRow
                key={projectAllocation.projectId ?? "no-project"}
                weeks={weeks}
                className="employee-row--sub"
                totalWeeklyHours={model.employee.weeklyCapacityHours}
                projectAllocations={[projectAllocation]}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
}
